import {RollPositionsInputs, Weekday} from '../../adjustment-pricing-grid/copilot/apg-copilot-domain';
import {dayKey, parseDayKey} from '../../adjustment-pricing-grid/copilot/apg-copilot-questionnaire';
import {MarketConditionOverride, MarketConditionOverrides} from '../../copilot-shared/opg-copilot-domain';
import {mavenRangeMode, MavenSetupInputs, MavenSideInputs} from '../maven-domain';
import {MavenTemplateDates, MavenTemplateIndicators, MavenTemplateSettings, MavenTemplateSide} from './maven-settings-template';

export function defaultMavenTemplateDates(): MavenTemplateDates {
    return {startOffsetDays: 0, rangeDays: 21, weekdayExclusions: []};
}

function calendarNumber(date: Date): number {
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000;
}

function plusDays(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function captureMavenIndicators(value: MavenTemplateIndicators | null): MavenTemplateIndicators | null {
    return value ? {ivCall: value.ivCall, ivPut: value.ivPut, hsCall: value.hsCall,
        hsPut: value.hsPut, vsCall: value.vsCall, vsPut: value.vsPut} : null;
}

function captureSide(side: MavenSideInputs): MavenTemplateSide {
    return {enabled: side.enabled, mode: side.mode || 'custom', presetId: side.presetId || null,
        coverage: side.coverage.kind, offsetFromShorts: side.offsetFromShorts,
        rangeMode: side.coverage.kind === 'range' ? mavenRangeMode(side.coverage) : undefined,
        rangeSize: side.coverage.kind === 'range' && mavenRangeMode(side.coverage) === 'distance'
            ? side.coverage.rangeSize == null ? null : side.coverage.rangeSize : null,
        minProfit: {amount: side.minProfit.amount, normalized: side.minProfit.normalized},
        maxRisk: side.maxRisk.kind === 'relative'
            ? {kind: 'relative', level: side.maxRisk.level}
            : {kind: 'dollar', amount: side.maxRisk.amount, normalized: side.maxRisk.normalized}};
}

export function captureMavenTemplate(setup: MavenSetupInputs, anchor: string | null,
    allDatesOverride: MavenTemplateIndicators | null, previousDates?: MavenTemplateDates): MavenTemplateSettings {
    const prior = previousDates || defaultMavenTemplateDates();
    let dates: MavenTemplateDates = {startOffsetDays: prior.startOffsetDays, rangeDays: prior.rangeDays,
        weekdayExclusions: setup.dates.weekdayExclusions.slice()};
    const selection = setup.dates.date;
    const base = anchor ? parseDayKey(anchor) : null;
    if (selection.kind === 'range' && base && selection.start && selection.end) {
        if (!(selection.start instanceof Date) || !(selection.end instanceof Date)
            || !isFinite(selection.start.getTime()) || !isFinite(selection.end.getTime())
            || selection.start.getTime() >= selection.end.getTime()) {
            throw new Error('Template Dates: Choose a Valid Start and a Later End for the Relative Range.');
        }
        dates = {startOffsetDays: Math.max(0, calendarNumber(selection.start) - calendarNumber(base)),
            rangeDays: Math.max(1, calendarNumber(selection.end) - calendarNumber(selection.start)),
            weekdayExclusions: setup.dates.weekdayExclusions.slice()};
    }
    return {dates, existingHedges: setup.existingHedges, calls: captureSide(setup.calls),
        puts: captureSide(setup.puts), allDatesOverride: captureMavenIndicators(allDatesOverride)};
}

// Boundaries must be listed weekdays, including the anchor itself when eligible. Calendar-day
// arithmetic is deliberate: DST must not turn a three-week preference into 20 or 22 days.
export function resolveMavenTemplateDates(settings: MavenTemplateDates, anchor: string | null,
    expirations: string[], maxRangeDays: number | null): RollPositionsInputs {
    const weekdays: Weekday[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    const excluded = settings.weekdayExclusions.slice();
    const result: RollPositionsInputs = {date: {kind: 'range', start: null, end: null},
        weekdayExclusions: excluded, specificDateExclusions: [], weekdayOverrides: [],
        bias: {enabled: false, direction: null}};
    const base = anchor ? parseDayKey(anchor) : null;
    if (!base) { return result; }
    const first = dayKey(plusDays(base, settings.startOffsetDays));
    const eligible = expirations.filter(key => {
        const d = parseDayKey(key);
        return d && d.getDay() > 0 && d.getDay() < 6 && excluded.indexOf(weekdays[d.getDay() - 1]) < 0;
    }).sort();
    const startKey = eligible.filter(key => key >= first)[0];
    if (!startKey) { return result; }
    const start = parseDayKey(startKey);
    const span = Math.min(settings.rangeDays, maxRangeDays == null ? settings.rangeDays : maxRangeDays);
    const endLimit = dayKey(plusDays(start, span));
    const ends = eligible.filter(key => key > startKey && key <= endLimit);
    result.date = {kind: 'range', start, end: ends.length ? parseDayKey(ends[ends.length - 1]) : null};
    return result;
}

export function restoreMavenSide(side: MavenTemplateSide): MavenSideInputs {
    const mode = side.rangeMode || 'endStrike';
    return {enabled: side.enabled, mode: side.mode || 'custom', presetId: side.presetId || null, presetValid: false,
        coverage: {kind: side.coverage, endStrike: null,
        rangeMode: side.coverage === 'range' ? mode : 'endStrike',
        rangeSize: side.coverage === 'range' && mode === 'distance'
            ? side.rangeSize == null ? null : side.rangeSize : null},
        offsetFromShorts: side.offsetFromShorts,
        minProfit: {...side.minProfit}, maxRisk: {...side.maxRisk}};
}

export function expandMavenIndicators(value: MavenTemplateIndicators | null, dates: string[]): MarketConditionOverrides {
    const map: MarketConditionOverrides = {};
    if (value) {
        dates.forEach(key => {
            map[key] = {...captureMavenIndicators(value), edited: true, ivLoading: false, vsLoading: false} as MarketConditionOverride;
        });
    }
    return map;
}
